import { CircleAlert } from 'lucide-react';
import { forwardRef, useId, type TextareaHTMLAttributes } from 'react';
import { cx } from '../cx.js';

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
  error?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea({ label, hint, error, id, className, rows = 4, ...rest }, ref) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = `${inputId}-aiuto`;
  const errorId = `${inputId}-errore`;
  return (
    <div className={cx('flex flex-col gap-2', className)}>
      <label htmlFor={inputId} className="t-label text-ink">
        {label}
      </label>
      <textarea
        ref={ref}
        id={inputId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={[error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined}
        className={cx('w-full rounded-md bg-surface text-ink px-4 py-3 border t-body resize-y', error ? 'border-error' : 'border-border-strong')}
        {...rest}
      />
      {error && (
        <p id={errorId} role="alert" className="t-body-small text-error flex items-start gap-1">
          <CircleAlert aria-hidden size={16} strokeWidth={1.75} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </p>
      )}
      {hint && (
        <p id={hintId} className="t-body-small text-ink-muted">
          {hint}
        </p>
      )}
    </div>
  );
});
